/**
 * تبدیل تاریخ شمسی و میلادی
 * ===========================
 * ورودی تاریخ در پنل باید شمسی باشد؛ مدیر ایرانی «۱۴۰۵/۰۵/۲۴» می‌نویسد، نه
 * «2026-08-15». ولی مرورگر و سرور فقط میلادی می‌فهمند.
 *
 * برای **نمایش** (میلادی ← شمسی) از `Intl` استفاده می‌شود که خودِ مرورگر
 * پیاده کرده و قابل اعتماد است. برای **ورودی** (شمسی ← میلادی) الگوریتم
 * استاندارد تبدیل روزِ ژولینی پیاده شده است.
 *
 * صحت این تبدیل با آزمون رفت‌وبرگشتی روی چند هزار روز پیاپی سنجیده می‌شود
 * (tests/jalali.mjs): هر تاریخ میلادی → شمسی → میلادی باید به خودش برگردد،
 * و خروجی شمسی باید با چیزی که `Intl` می‌گوید یکی باشد.
 */

const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹'

export const toEn = (s: string): string =>
  String(s ?? '').replace(/[۰-۹]/g, (d) => String(FA_DIGITS.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))

export const toFa = (s: string | number): string =>
  String(s ?? '').replace(/\d/g, (d) => FA_DIGITS[Number(d)])

/** شماره روز ژولینی از تاریخ میلادی */
function gregorianToJdn(gy: number, gm: number, gd: number): number {
  const a = Math.floor((14 - gm) / 12)
  const y = gy + 4800 - a
  const m = gm + 12 * a - 3
  return gd + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4)
    - Math.floor(y / 100) + Math.floor(y / 400) - 32045
}

function jdnToGregorian(jdn: number): [number, number, number] {
  let j = jdn + 32044
  const g = Math.floor(j / 146097)
  const dg = j % 146097
  const c = Math.floor((Math.floor(dg / 36524) + 1) * 3 / 4)
  const dc = dg - c * 36524
  const b = Math.floor(dc / 1461)
  const db = dc % 1461
  const a = Math.floor((Math.floor(db / 365) + 1) * 3 / 4)
  const da = db - a * 365
  const y = g * 400 + c * 100 + b * 4 + a
  const m = Math.floor((da * 5 + 308) / 153) - 2
  const d = da - Math.floor((m + 4) * 153 / 5) + 122
  return [y - 4800 + Math.floor((m + 2) / 12), ((m + 2) % 12) + 1, d + 1]
}

/** شماره روز ژولینی از تاریخ شمسی */
function jalaliToJdn(jy: number, jm: number, jd: number): number {
  const epBase = jy - (jy >= 0 ? 474 : 473)
  const epYear = 474 + (((epBase % 2820) + 2820) % 2820)
  return jd
    + (jm <= 7 ? (jm - 1) * 31 : (jm - 1) * 30 + 6)
    + Math.floor((epYear * 682 - 110) / 2816)
    + (epYear - 1) * 365
    + Math.floor(epBase / 2820) * 1029983
    // مبدأ تقویم هجری شمسی بر حسب روز ژولینی.
    // این عدد در منابع مختلف گاهی ۱۹۴۸۳۱۹ نوشته می‌شود (بسته به اینکه روز
    // ژولینی از ظهر شمرده شود یا نیمه‌شب). مقدار درست برای این پیاده‌سازی با
    // لنگرِ «۱ فروردین ۱۴۰۵ = ۲۱ مارس ۲۰۲۶» سنجیده و در آزمون قفل شده است.
    + 1948320
}

function jdnToJalali(jdn: number): [number, number, number] {
  const depoch = jdn - jalaliToJdn(475, 1, 1)
  const cycle = Math.floor(depoch / 1029983)
  const cyear = ((depoch % 1029983) + 1029983) % 1029983
  const ycycle = cyear === 1029982
    ? 2820
    : Math.floor((Math.floor(cyear / 366) * 2134
                  + (cyear % 366) * 2816 + 2815) / 1028522) + Math.floor(cyear / 366) + 1
  let jy = ycycle + 2820 * cycle + 474
  if (jy <= 0) jy -= 1
  const yday = jdn - jalaliToJdn(jy, 1, 1) + 1
  const jm = yday <= 186 ? Math.ceil(yday / 31) : Math.ceil((yday - 6) / 30)
  const jd = jdn - jalaliToJdn(jy, jm, 1) + 1
  return [jy, jm, jd]
}

export type Jalali = { jy: number; jm: number; jd: number }

/**
 * تقویم مرجع.
 *
 * الگوریتم حسابیِ بالا (چرخه ۲۸۲۰ ساله) در تشخیص سال کبیسه گاهی با تقویم
 * **رسمی ایران** یک روز اختلاف پیدا می‌کند — مثلاً ۳۰ اسفند ۱۴۰۳. تقویم رسمی
 * نجومی است و همان چیزی است که تلفن و تقویم دیواری کاربر نشان می‌دهد، و
 * `Intl` هم دقیقاً همان را پیاده کرده است.
 *
 * پس مرجع را `Intl` می‌گیریم و الگوریتم حسابی فقط برای «حدس اولیه» در تبدیل
 * معکوس به کار می‌رود. اگر محیطی تقویم فارسی نداشته باشد، به‌طور خودکار به
 * همان الگوریتم حسابی برمی‌گردیم.
 */
let _fmt: Intl.DateTimeFormat | null = null
let _fmtReady: boolean | null = null

function persianFormatter(): Intl.DateTimeFormat | null {
  if (_fmtReady !== null) return _fmt
  try {
    const f = new Intl.DateTimeFormat('en-u-ca-persian-nu-latn', {
      year: 'numeric', month: 'numeric', day: 'numeric',
    })
    // بررسی می‌کنیم که واقعاً تقویم فارسی را پشتیبانی کند
    const parts = f.formatToParts(new Date(2026, 2, 21))
    const y = Number(parts.find((p) => p.type === 'year')?.value)
    _fmt = y > 1300 && y < 1500 ? f : null
  } catch {
    _fmt = null
  }
  _fmtReady = true
  return _fmt
}

export function toJalali(date: Date): Jalali {
  const f = persianFormatter()
  if (f) {
    const parts = f.formatToParts(date)
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
    const jy = get('year'), jm = get('month'), jd = get('day')
    if (Number.isFinite(jy) && Number.isFinite(jm) && Number.isFinite(jd)) {
      return { jy, jm, jd }
    }
  }
  const [jy, jm, jd] = jdnToJalali(
    gregorianToJdn(date.getFullYear(), date.getMonth() + 1, date.getDate()))
  return { jy, jm, jd }
}

export function fromJalali(jy: number, jm: number, jd: number,
                           hh = 0, mm = 0): Date {
  const [gy, gm, gd] = jdnToGregorian(jalaliToJdn(jy, jm, jd))
  const guess = new Date(gy, gm - 1, gd, hh, mm, 0, 0)
  if (!persianFormatter()) return guess

  // حدس حسابی حداکثر چند روز خطا دارد؛ اطرافش را می‌گردیم تا روزی را پیدا
  // کنیم که تقویم مرجع دقیقاً همان تاریخ شمسی را برایش می‌دهد.
  for (const delta of [0, -1, 1, -2, 2, -3, 3, -4, 4]) {
    const probe = new Date(gy, gm - 1, gd + delta, hh, mm, 0, 0)
    const back = toJalali(probe)
    if (back.jy === jy && back.jm === jm && back.jd === jd) return probe
  }
  return guess
}

export function isValidJalali(jy: number, jm: number, jd: number): boolean {
  if (!Number.isInteger(jy) || !Number.isInteger(jm) || !Number.isInteger(jd)) return false
  if (jy < 1 || jm < 1 || jm > 12 || jd < 1 || jd > 31) return false
  if (jm > 6 && jd > 30) return false
  // روز ۳۰ اسفند فقط در سال کبیسه هست؛ به‌جای جدول کبیسه، رفت‌وبرگشت را
  // بررسی می‌کنیم — همیشه درست است و نیاز به نگهداری جدول ندارد.
  const back = toJalali(fromJalali(jy, jm, jd))
  return back.jy === jy && back.jm === jm && back.jd === jd
}

const PAD = (n: number) => String(n).padStart(2, '0')

/** «۱۴۰۵/۰۵/۲۴ ۱۴:۳۰» برای نمایش */
export function formatJalali(date: Date, withTime = true): string {
  const { jy, jm, jd } = toJalali(date)
  const base = `${jy}/${PAD(jm)}/${PAD(jd)}`
  return toFa(withTime ? `${base} ${PAD(date.getHours())}:${PAD(date.getMinutes())}` : base)
}

/**
 * تفسیر آنچه کاربر تایپ کرده: «۱۴۰۵/۵/۲۴» یا «1405-05-24 14:30».
 * خروجی null یعنی قابل تفسیر نیست.
 */
export function parseJalaliInput(text: string): Date | null {
  const raw = toEn(String(text || '')).trim()
  if (!raw) return null
  const m = /^(\d{3,4})[/\-.](\d{1,2})[/\-.](\d{1,2})(?:[\sT]+(\d{1,2}):(\d{1,2}))?/.exec(raw)
  if (!m) return null
  const [jy, jm, jd] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const hh = m[4] === undefined ? 0 : Number(m[4])
  const mi = m[5] === undefined ? 0 : Number(m[5])
  if (hh > 23 || mi > 59) return null
  if (!isValidJalali(jy, jm, jd)) return null
  return fromJalali(jy, jm, jd, hh, mi)
}

/** خروجی محلی به شکل `YYYY-MM-DDTHH:mm` برای ارسال به سرور */
export function toLocalIso(date: Date): string {
  return `${date.getFullYear()}-${PAD(date.getMonth() + 1)}-${PAD(date.getDate())}`
       + `T${PAD(date.getHours())}:${PAD(date.getMinutes())}`
}

export const JALALI_MONTHS = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
]
